// A pull request's size and the viewer's review progress, summarised when
// they open it, for the dashboard to show without reading its diff. Pure.
import { compactCount } from './stackRail';

/** What the dashboard shows of a pull request the viewer opened: counts at one head commit. */
export interface PrCounts {
  /** Changed files (not those changed only by a rebase). */
  files: number;
  /** Change blocks: the engine's, for files whose diff was computed, else GitHub's hunks. */
  blocks: number;
  /** Removed and added lines, and those the viewer's review marks cover, as the PR page's meter counts them. */
  lines: number;
  reviewed: number;
}

/**
 * Hunks in a unified patch: its `@@` header lines. A changed file without
 * a patch (binary, or too large for GitHub to send one) is one block.
 */
export function hunkCount(patch: string | null, changed = true): number {
  if (!patch) return changed ? 1 : 0;
  let n = 0;
  for (const line of patch.split('\n')) if (line.startsWith('@@')) n++;
  return n || (changed ? 1 : 0);
}

/**
 * The counts over a pull request's changed files. `progressOf` is the PR
 * page's per-file progress (the meter's numbers); `blocksOf` the file's
 * computed change blocks, or null before its diff is computed.
 */
export function summarize(
  entries: readonly { path: string; patch: string | null; noise: boolean }[],
  progressOf: (path: string) => { total: number; done: number },
  blocksOf: (path: string) => number | null,
): PrCounts {
  const out: PrCounts = { files: 0, blocks: 0, lines: 0, reviewed: 0 };
  for (const e of entries) {
    if (e.noise) continue;
    const p = progressOf(e.path);
    out.files++;
    out.blocks += blocksOf(e.path) ?? hunkCount(e.patch);
    out.lines += p.total;
    out.reviewed += p.done;
  }
  return out;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

/** The counts in words: brief for the row (`lines`, `blocks`), in full for its label, and the share reviewed (1 with nothing to review). */
export function progressWords(c: PrCounts): { lines: string; blocks: string; share: number; full: string } {
  return {
    lines: `${compactCount(c.reviewed)}/${compactCount(c.lines)}`,
    blocks: plural(c.blocks, 'block'),
    share: c.lines ? c.reviewed / c.lines : 1,
    full: `${c.reviewed.toLocaleString('en-US')} of ${plural(c.lines, 'line')} reviewed; ${plural(c.blocks, 'change block')} in ${plural(c.files, 'file')}`,
  };
}
